import { useState, useEffect } from "react";
import {
  getDepartmentCategories,
  createDepartmentCategory,
  updateDepartmentCategory,
  deleteDepartmentCategory,
  getDepartments,
  createDepartment,
  deleteDepartment,
} from "../api";
import "./DepartmentManager.css";
import { toast } from "./Toast";

export default function DepartmentManager() {
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCatForm, setShowCatForm] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingCat, setEditingCat] = useState(null);

  // Category form
  const [catName, setCatName] = useState("");
  const [catNameKa, setCatNameKa] = useState("");
  const [catDesc, setCatDesc] = useState("");

  // User form
  const [userEmail, setUserEmail] = useState("");
  const [userDeptName, setUserDeptName] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [userCategoryId, setUserCategoryId] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [cats, depts] = await Promise.all([getDepartmentCategories(), getDepartments()]);
      setCategories(Array.isArray(cats) ? cats : cats.results || []);
      setDepartments(Array.isArray(depts) ? depts : depts.results || []);
    } catch (err) {
      toast(err.message, "error");
    }
    setLoading(false);
  }

  async function handleSaveCategory() {
    if (!catName.trim() || !catNameKa.trim()) {
      toast("სახელი სავალდებულოა", "error");
      return;
    }
    try {
      const data = { name: catName, name_ka: catNameKa, description: catDesc, is_active: true };
      if (editingCat) {
        await updateDepartmentCategory(editingCat.id, data);
        toast("კატეგორია განახლდა", "success");
      } else {
        await createDepartmentCategory(data);
        toast("კატეგორია დაემატა", "success");
      }
      resetCatForm();
      loadData();
    } catch (err) {
      toast(err.message, "error");
    }
  }

  async function handleDeleteCategory(id) {
    const usersInCat = departments.filter(d => d.category === id);
    if (usersInCat.length > 0) {
      toast("ჯერ წაშალეთ ამ კატეგორიის მომხმარებლები", "error");
      return;
    }
    if (!window.confirm("კატეგორიის წაშლა?")) return;
    try {
      await deleteDepartmentCategory(id);
      toast("კატეგორია წაიშალა", "success");
      loadData();
    } catch (err) {
      toast(err.message, "error");
    }
  }

  function startEditCategory(cat) {
    setEditingCat(cat);
    setCatName(cat.name);
    setCatNameKa(cat.name_ka);
    setCatDesc(cat.description);
    setShowCatForm(true);
  }

  function resetCatForm() {
    setEditingCat(null);
    setCatName("");
    setCatNameKa("");
    setCatDesc("");
    setShowCatForm(false);
  }

  async function handleCreateUser() {
    if (!userEmail.trim() || !userDeptName.trim() || !userPassword.trim()) {
      toast("ყველა ველი სავალდებულოა", "error");
      return;
    }
    if (userPassword.length < 8) {
      toast("პაროლი მინიმუმ 8 სიმბოლო უნდა იყოს", "error");
      return;
    }
    try {
      await createDepartment({
        email: userEmail,
        department_name: userDeptName,
        password: userPassword,
        password_confirm: userPassword,
        category_id: userCategoryId || null,
      });
      toast("მომხმარებელი შეიქმნა", "success");
      setUserEmail("");
      setUserDeptName("");
      setUserPassword("");
      setUserCategoryId("");
      setShowUserForm(false);
      loadData();
    } catch (err) {
      toast(err.message, "error");
    }
  }

  async function handleDeleteUser(dept) {
    if (dept.is_superuser) {
      toast("ადმინის წაშლა შეუძლებელია", "error");
      return;
    }
    if (!window.confirm(`წაშალოთ მომხმარებელი "${dept.department_name}"?`)) return;
    try {
      await deleteDepartment(dept.id);
      toast("მომხმარებელი წაიშალა", "success");
      loadData();
    } catch (err) {
      toast(err.message, "error");
    }
  }

  // Group users by category for display
  function getUsersForCategory(catId) {
    return departments.filter(d => d.category === catId);
  }
  const uncategorizedUsers = departments.filter(d => !d.category);

  if (loading) return <div className="dm-loading">იტვირთება...</div>;

  return (
    <div className="dept-manager">
      {/* ──── Categories Section ──── */}
      <div className="dm-section">
        <div className="dm-section-header">
          <div>
            <h3>დეპარტამენტები</h3>
            <p className="dm-section-hint">დეპარტამენტი = წერილების კატეგორია. AI ავტომატურად ანაწილებს წერილებს დეპარტამენტებში.</p>
          </div>
          <button className="dm-add-btn" onClick={() => setShowCatForm(true)}>
            + ახალი დეპარტამენტი
          </button>
        </div>

        {showCatForm && (
          <div className="dm-form">
            <div className="dm-form-title">{editingCat ? "დეპარტამენტის რედაქტირება" : "ახალი დეპარტამენტის დამატება"}</div>
            <div className="dm-form-grid">
              <div className="dm-field">
                <label>სახელი (ინგლ.)</label>
                <input
                  placeholder="მაგ: IT, Finance, Legal"
                  value={catName}
                  onChange={(e) => setCatName(e.target.value)}
                />
              </div>
              <div className="dm-field">
                <label>სახელი (ქართ.)</label>
                <input
                  placeholder="მაგ: IT, ფინანსები, იურიდიული"
                  value={catNameKa}
                  onChange={(e) => setCatNameKa(e.target.value)}
                />
              </div>
            </div>
            <div className="dm-field">
              <label>აღწერა (არასავალდებულო)</label>
              <input
                placeholder="რა ტიპის წერილები მიდის ამ დეპარტამენტში"
                value={catDesc}
                onChange={(e) => setCatDesc(e.target.value)}
              />
            </div>
            <div className="dm-form-actions">
              <button className="dm-save-btn" onClick={handleSaveCategory}>
                {editingCat ? "განახლება" : "დამატება"}
              </button>
              <button className="dm-cancel-btn" onClick={resetCatForm}>გაუქმება</button>
            </div>
          </div>
        )}

        {categories.length === 0 ? (
          <div className="dm-empty">დეპარტამენტები არ არის. დაამატეთ პირველი.</div>
        ) : (
          <div className="dm-cards">
            {categories.map((cat) => {
              const users = getUsersForCategory(cat.id);
              return (
                <div key={cat.id} className="dm-card">
                  <div className="dm-card-header">
                    <div>
                      <span className="dm-card-name">{cat.name_ka || cat.name}</span>
                      {cat.name_ka && <span className="dm-card-name-en">{cat.name}</span>}
                    </div>
                    <div className="dm-card-actions">
                      <button className="dm-edit-btn" onClick={() => startEditCategory(cat)}>რედაქტირება</button>
                      <button className="dm-del-btn" onClick={() => handleDeleteCategory(cat.id)}>წაშლა</button>
                    </div>
                  </div>
                  {cat.description && <div className="dm-card-desc">{cat.description}</div>}
                  <div className="dm-card-users">
                    <span className="dm-card-users-label">წევრები ({users.length}):</span>
                    {users.length > 0 ? (
                      <div className="dm-user-chips">
                        {users.map(u => (
                          <span key={u.id} className="dm-user-chip">
                            {u.department_name}
                            <span className="dm-user-chip-email">{u.email}</span>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="dm-no-users">წევრები არ ჰყავს</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ──── Users Section ──── */}
      <div className="dm-section">
        <div className="dm-section-header">
          <div>
            <h3>მომხმარებლები</h3>
            <p className="dm-section-hint">თითოეულ მომხმარებელს აქვს ცალკე ელ.ფოსტა და პაროლი. მიებმება დეპარტამენტს და ხედავს მხოლოდ თავისი დეპარტამენტის წერილებს.</p>
          </div>
          <button className="dm-add-btn" onClick={() => setShowUserForm(true)}>
            + ახალი მომხმარებელი
          </button>
        </div>

        {showUserForm && (
          <div className="dm-form">
            <div className="dm-form-title">ახალი მომხმარებლის დამატება</div>
            <div className="dm-form-grid">
              <div className="dm-field">
                <label>ელ. ფოსტა (ავტორიზაციისთვის)</label>
                <input
                  type="email"
                  placeholder="მაგ: it@testcredobank.ge"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                />
              </div>
              <div className="dm-field">
                <label>საჩვენებელი სახელი</label>
                <input
                  placeholder="მაგ: IT დეპარტამენტი"
                  value={userDeptName}
                  onChange={(e) => setUserDeptName(e.target.value)}
                />
              </div>
            </div>
            <div className="dm-form-grid">
              <div className="dm-field">
                <label>პაროლი (მინ. 8 სიმბოლო)</label>
                <input
                  type="password"
                  placeholder="პაროლი"
                  value={userPassword}
                  onChange={(e) => setUserPassword(e.target.value)}
                />
              </div>
              <div className="dm-field">
                <label>დეპარტამენტი</label>
                <select
                  value={userCategoryId}
                  onChange={(e) => setUserCategoryId(e.target.value)}
                >
                  <option value="">-- აირჩიეთ დეპარტამენტი --</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name_ka || cat.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="dm-form-actions">
              <button className="dm-save-btn" onClick={handleCreateUser}>შექმნა</button>
              <button className="dm-cancel-btn" onClick={() => setShowUserForm(false)}>გაუქმება</button>
            </div>
          </div>
        )}

        <table className="dm-table">
          <thead>
            <tr>
              <th>საჩვენებელი სახელი</th>
              <th>ელ. ფოსტა</th>
              <th>დეპარტამენტი</th>
              <th>როლი</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {departments.filter(d => !d.is_superuser).map((dept) => (
              <tr key={dept.id}>
                <td className="dm-td-name">{dept.department_name}</td>
                <td>{dept.email}</td>
                <td>
                  {dept.category_name ? (
                    <span className="dm-cat-badge">{dept.category_name}</span>
                  ) : (
                    <span className="dm-no-cat">მიუბმელი</span>
                  )}
                </td>
                <td>
                  {dept.is_superuser ? (
                    <span className="dm-role-admin">ადმინი</span>
                  ) : (
                    <span className="dm-role-user">თანამშრომელი</span>
                  )}
                </td>
                <td>
                  {!dept.is_superuser && (
                    <button className="dm-del-btn" onClick={() => handleDeleteUser(dept)}>წაშლა</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {uncategorizedUsers.length > 0 && !uncategorizedUsers.every(u => u.is_superuser) && (
          <div className="dm-warning">
            ყურადღება: ზოგიერთ მომხმარებელს არ აქვს მინიჭებული დეპარტამენტი. მათ არ ეჩვენებათ წერილები.
          </div>
        )}
      </div>
    </div>
  );
}
